import { View, Text, Image } from 'react-native'
import { useRef, useState, useEffect, useMemo } from 'react'
import { Button } from '@/src/common/components/Button'
import { Check } from '@/src/assets/icons/Check'
import { router } from 'expo-router'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useLogError } from '@/src/common/hooks/useLogError'
import { triggerSegmentAction } from '@/src/services/segment'
import { useCardBenefitsParameterStore } from '../statsig/store/useCardBenefitsParameterStore'
import { CardBenefitsComponents as Components } from '../components'
import BottomSheet from '@gorhom/bottom-sheet'
import {
  BenefitModal,
  useIsBenefitScreenPresentedAsModal,
} from '@/src/experiences/card-benefits/components/BenefitModal'
import { vibrateSuccess } from '@/src/common/utils/vibrate'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'
import { useReferralStats } from '@/src/common/hooks/useReferralStats'
import { REFERRAL_BONUS_OFFER_PARTNER_NAME } from '../constants'
import { useGetCardAccount } from '../hooks/useGetCardAccount'
import { useBenefits } from '../hooks/useBenefits'
import { useBenefitsEnrollments } from '../hooks/useBenefitsEnrollments'
import * as Stores from '../stores'

const USER_STATES = {
  NON_CARD_MEMBER: 'NonCardMember',
  CARD_MEMBER_ENROLLMENT_INELIGIBLE: 'CardMemberIneligible',
  CARD_MEMBER_ENROLLMENT_ELIGIBLE: 'CardMemberEligible',
  CARD_MEMBER_ENROLLED: 'CardMemberEnrolled',
  CARD_MEMBER_MET_SPEND_REQUIREMENT: 'CardMemberMetSpendRequirement',
  CARD_MEMBER_OFFER_EXPIRED: 'CardMemberOfferExpired',
} as const

export function ReferralBonusOffer() {
  const onError = useLogError()
  const Alert = useAlert(ReferralBonusOffer.name)

  const calculateOfferExpirationDate = (enrolledAt: string) => {
    const enrolledDate = new Date(enrolledAt)
    const expirationDate = new Date(enrolledDate)
    expirationDate.setDate(enrolledDate.getDate() + 90)
    return expirationDate
  }

  const cardBenefitsStore = useCardBenefitsParameterStore()
  const cardBenefitsCMS = cardBenefitsStore.get('benefits_v1')
  const selectedBenefit = cardBenefitsCMS?.[REFERRAL_BONUS_OFFER_PARTNER_NAME]

  const enrollmentModalRef = useRef<BottomSheet>(null)
  const nonCardModalRef = useRef<BottomSheet>(null)
  const [benefitId, setBenefitId] = useState<string>()
  const [creditRedemption, setCreditRedemption] = useState<string>()
  const [creditPerRefresh, setCreditPerRefresh] = useState<number | null>()

  const isBenefitScreenPresentedAsModal = useIsBenefitScreenPresentedAsModal()

  const { data: referralStats } = useReferralStats()
  const referralCount = referralStats?.getReferralStats.CARD.numberOfReferredMembers || 0

  const { data: benefitEnrollmentsData } = useBenefitsEnrollments()
  const benefitEnrollment = benefitEnrollmentsData?.find(
    (benefitEnrollment) => benefitEnrollment.benefitId === benefitId,
  )
  const isEnrolled = benefitEnrollment?.isEnrolled || false

  const cardAccount = useGetCardAccount()
  const isNonCardMember = !cardAccount.data

  const [
    activateReferralBonusOffer,
    {
      data: activateReferralBonusOfferData,
      isSuccess: activateReferralBonusOfferIsSuccess,
      error: activateReferralBonusOfferError,
      isLoading: activateReferralBonusOfferIsLoading,
    },
  ] = Stores.CardBenefitsApi.endpoints['POST /benefits/referral-bonus/activate'].useMutation()

  useEffect(() => {
    if (activateReferralBonusOfferIsSuccess) {
      try {
        void vibrateSuccess()
      } finally {
        enrollmentModalRef.current?.snapToIndex(0)
      }
    }
  }, [activateReferralBonusOfferIsSuccess])

  useEffect(() => {
    if (activateReferralBonusOfferError) {
      Alert.alert('Your offer could not be activated at this time', 'Please try again later')
      onError(activateReferralBonusOfferError)
    }
  }, [
    activateReferralBonusOfferIsSuccess,
    activateReferralBonusOfferData,
    activateReferralBonusOfferError,
    Alert,
    onError,
  ])

  const { data: benefitsData } = useBenefits()
  const benefit = benefitsData?.find(
    (benefit) => benefit.partnerName === REFERRAL_BONUS_OFFER_PARTNER_NAME,
  )
  useEffect(() => {
    if (benefit) {
      setBenefitId(benefit.id)
      setCreditPerRefresh(benefit.creditPerRefresh ?? null)
      setCreditRedemption(benefit.creditRedemption ?? undefined)
    }
  }, [benefit])

  const userState = useMemo(() => {
    const currentSpend = parseFloat(creditRedemption ?? '0')
    const requiredSpend = creditPerRefresh ?? 10000

    // Order of if statements is important
    if (isNonCardMember) return USER_STATES.NON_CARD_MEMBER

    if (currentSpend >= requiredSpend) return USER_STATES.CARD_MEMBER_MET_SPEND_REQUIREMENT

    if (isEnrolled && benefitEnrollment?.enrolledAt) {
      // Check if offer has expired and spending requirement not met
      const expirationDate = calculateOfferExpirationDate(benefitEnrollment.enrolledAt)
      const currentDate = new Date()
      const hasExpired = currentDate > expirationDate

      const didNotMeetRequirement = currentSpend < requiredSpend

      return hasExpired && didNotMeetRequirement
        ? USER_STATES.CARD_MEMBER_OFFER_EXPIRED
        : USER_STATES.CARD_MEMBER_ENROLLED
    }

    return referralCount >= 2
      ? USER_STATES.CARD_MEMBER_ENROLLMENT_ELIGIBLE
      : USER_STATES.CARD_MEMBER_ENROLLMENT_INELIGIBLE
  }, [
    isNonCardMember,
    referralCount,
    isEnrolled,
    benefitEnrollment?.enrolledAt,
    creditRedemption,
    creditPerRefresh,
  ])

  if (!selectedBenefit) {
    return null
  }

  const getSummaryText = () => {
    switch (userState) {
      case USER_STATES.CARD_MEMBER_OFFER_EXPIRED:
        return 'You did not meet the spend requirements in time. You can still earn Mesa Points by referring friends.'
      case USER_STATES.CARD_MEMBER_ENROLLED: {
        if (benefitEnrollment?.enrolledAt) {
          const expirationDate = calculateOfferExpirationDate(benefitEnrollment.enrolledAt)
          const formattedDate = expirationDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })

          return `Your offer is currently active! \n\nEarn 50,000 Mesa Points when you spend $10,000 within 90 days of activation of this offer. You have until ${formattedDate} to spend $10,000.`
        } else {
          return 'Your offer is currently active! \n\nEarn 50,000 Mesa Points when you spend $10,000 within 90 days of activation of this offer.'
        }
      }
      case USER_STATES.CARD_MEMBER_ENROLLMENT_ELIGIBLE:
        return "You're in, thank you for referring! \n \n Activate your bonus offer whenever you're ready to start. Earn 50,000 Mesa Points when you spend $10,000 within 90 days of activation."
      case USER_STATES.CARD_MEMBER_MET_SPEND_REQUIREMENT:
        return ' 🎉 Congratulations! 🎉 \n\n You’ve Earned 50,000 Mesa Points! \n\n You met the $10,000 spending requirement and your 50,000 bonus points are on the way. You can earn more points by referring more friends.'
      case USER_STATES.NON_CARD_MEMBER:
      case USER_STATES.CARD_MEMBER_ENROLLMENT_INELIGIBLE:
        return selectedBenefit.summary
      default:
        userState satisfies never
        return selectedBenefit.summary
    }
  }

  const ctaText = () => {
    switch (userState) {
      case USER_STATES.NON_CARD_MEMBER:
        return 'Get Mesa Homeowners Card'
      case USER_STATES.CARD_MEMBER_ENROLLMENT_ELIGIBLE:
        return 'Activate'
      case USER_STATES.CARD_MEMBER_ENROLLED:
        return "You're Enrolled"
      case USER_STATES.CARD_MEMBER_ENROLLMENT_INELIGIBLE:
      case USER_STATES.CARD_MEMBER_OFFER_EXPIRED:
      case USER_STATES.CARD_MEMBER_MET_SPEND_REQUIREMENT:
        return 'Refer Friends'
      default:
        userState satisfies never
        return 'Refer Friends'
    }
  }

  const onPress = () => {
    switch (userState) {
      case USER_STATES.NON_CARD_MEMBER:
        router.dismissTo('/main/card-benefits')
        router.push('/main/card/cardBenefits')
        break
      case USER_STATES.CARD_MEMBER_ENROLLMENT_INELIGIBLE:
      case USER_STATES.CARD_MEMBER_OFFER_EXPIRED:
      case USER_STATES.CARD_MEMBER_MET_SPEND_REQUIREMENT:
        router.dismissTo('/main/card-benefits')
        router.push('/main/refer-a-friend')
        break
      case USER_STATES.CARD_MEMBER_ENROLLMENT_ELIGIBLE:
        void activateReferralBonusOffer({})
        break
      case USER_STATES.CARD_MEMBER_ENROLLED:
        return // Already enrolled, no action
      default:
        userState satisfies never
        break
    }
  }

  const enrollmentButtonDisabled =
    userState === USER_STATES.CARD_MEMBER_ENROLLED ||
    activateReferralBonusOfferIsLoading ||
    cardAccount.isUninitialized ||
    cardAccount.isLoading ||
    cardAccount.isError

  if (!benefitId) {
    return
  }

  return (
    <BenefitModal screenName="referral-bonus-offer">
      <Components.Container>
        <Components.Header title={selectedBenefit.header} screenName="referral-bonus-offer" />
        <Components.Scroll>
          <Components.Hero
            source={{ uri: selectedBenefit.thumbnail_img }}
            backgroundColor={selectedBenefit.theme.backgroundColor}
          />
          <View
            style={[
              { padding: 16, backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
              !isBenefitScreenPresentedAsModal && {
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              },
            ]}
          >
            <Components.Title title={selectedBenefit.title} />
            <Components.RedemptionProgress
              isEnrolled={isEnrolled}
              creditPerRefresh={creditPerRefresh ?? null}
              benefitId={benefitId}
              refreshes={null}
              creditRedemption={creditRedemption ?? null}
            />
            <Components.Summary summary={getSummaryText()} />

            {/* Referral Tracker */}
            <View style={{ marginVertical: 16 }}>
              <Text
                style={[
                  {
                    fontSize: 16,
                    fontFamily: fontFamilies['PlusJakartaSans-SemiBold'],
                    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
                    marginBottom: 12,
                  },
                ]}
              >
                Current Referrals ({referralCount})
              </Text>

              <View style={{ flexDirection: 'row', gap: 12 }}>
                {[0, 1].map((index) => (
                  <View
                    key={index}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 30,
                      opacity: index < referralCount ? 1 : 0.5,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image
                      source={require('@/src/assets/images/referral_icon.png')}
                      style={{
                        width: 32,
                        height: 32,
                      }}
                    />
                  </View>
                ))}
              </View>
            </View>

            <Button
              testID="activate-referral-bonus-offer"
              containerStyle={{ marginTop: 24, paddingHorizontal: 16 }}
              disabled={enrollmentButtonDisabled}
              style={{
                backgroundColor: enrollmentButtonDisabled
                  ? mesaColorNames['Mesa - Brand/White #FFFFFF']
                  : mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
              }}
              {...(enrollmentButtonDisabled
                ? { borderColor: mesaColorNames['Acre Turquoise/500 #00DFC9'], borderWidth: 1 }
                : undefined)}
              onPress={() => {
                onPress()
                void triggerSegmentAction({
                  component: 'Button',
                  action: 'User Enrolled In Benefit',
                  benefitName: 'Referral Bonus Offer',
                })
              }}
              loading={activateReferralBonusOfferIsLoading}
            >
              <>
                {isEnrolled && <Check height={20} width={20} />}
                <Text
                  style={[
                    {
                      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
                      fontWeight: 700,
                      fontSize: 16,
                      marginLeft: isEnrolled ? 8 : 0,
                    },
                    isEnrolled && {
                      color: mesaColorNames['Acre Turquoise/800 #086762'],
                    },
                  ]}
                >
                  {ctaText()}
                </Text>
              </>
            </Button>

            {userState === USER_STATES.CARD_MEMBER_ENROLLMENT_INELIGIBLE && (
              <Text
                style={[
                  {
                    textAlign: 'center',
                    marginTop: 8,
                    fontSize: 14,
                    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
                    color: mesaColorNames['Greyscale/400 #A4AAAC'],
                  },
                ]}
              >
                Refer two friends to unlock this offer
              </Text>
            )}
          </View>

          <Components.Terms terms={selectedBenefit.terms} />
        </Components.Scroll>

        <Components.EnrollmentSuccessModal
          selectedBenefit={selectedBenefit}
          bottomSheetRef={enrollmentModalRef}
        />

        <Components.NonMesaMemberModal bottomSheetRef={nonCardModalRef} />
      </Components.Container>
    </BenefitModal>
  )
}
